import {
	MAX_RECEIPT_ITEM_LENGTH,
	PAYMENT_MODES,
	PAYMENT_SUBJECTS,
	type PaymentMode,
	type PaymentSubject,
	type ReceiptConfig,
	TAX_SYSTEM_CODES,
	VAT_CODES,
} from "@velizhanin-dev/bot-scenario"
import { IssueText } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { NativeSelect } from "@/shared/ui/native-select"
import { VariableSelect } from "@/shared/ui/variable-select"
import { Checkbox } from "./checkbox-label"
import type { Config, EditorProps } from "./editor-props"
import { readString, withField } from "./editor-props"
import { issuesAt } from "./issues-text"
import {
	DEFAULT_RECEIPT,
	MODE_LABELS,
	SUBJECT_LABELS,
	TAX_LABELS,
	VAT_LABELS,
} from "./lib/invoice-labels"

/** Имя/телефон/email покупателя: галочка «спросить» + переменная, куда сохранить. */
export function ContactField({
	config,
	onChange,
	draft,
	field,
	label,
	requiredByReceipt,
}: Pick<EditorProps, "config" | "onChange" | "draft"> & {
	field: "Name" | "Phone" | "Email"
	label: string
	requiredByReceipt: boolean
}) {
	const needKey = `need${field}`
	const saveKey = `save${field}To`
	const asked = config[needKey] === true || requiredByReceipt
	return (
		<div className="space-y-1">
			<Checkbox
				label={requiredByReceipt ? `${label}: нужен для чека` : label}
				checked={asked}
				onChange={(checked) => {
					if (requiredByReceipt) return
					const next = withField(config, needKey, checked || undefined)
					onChange(checked ? next : withField(next, saveKey, undefined))
				}}
			/>
			{asked ? (
				<VariableSelect
					aria-label={`Куда сохранить: ${label}`}
					value={readString(config, saveKey)}
					onChange={(name) => onChange(withField(config, saveKey, name || undefined))}
					variables={draft.variables}
					emptyLabel="Не сохранять"
				/>
			) : null}
		</div>
	)
}

/** Чек по 54-ФЗ: галочка «отправлять» + параметры чека для ЮKassa. */
export function ReceiptFields({
	config,
	onChange,
	issues,
	receipt,
}: Pick<EditorProps, "config" | "onChange" | "issues"> & { receipt: ReceiptConfig | undefined }) {
	const setReceipt = (patch: Partial<ReceiptConfig>) =>
		onChange({ ...config, receipt: { ...(receipt ?? DEFAULT_RECEIPT), ...patch } })

	return (
		<>
			{receipt ? (
				<div className="space-y-2 rounded-lg border border-border/60 p-2">
					<NativeSelect
						aria-label="Ставка НДС"
						value={receipt.vatCode}
						onChange={(event) => setReceipt({ vatCode: Number(event.target.value) })}
					>
						{VAT_CODES.map((code) => (
							<option key={code} value={code}>
								{VAT_LABELS[code]}
							</option>
						))}
					</NativeSelect>
					<div className="grid grid-cols-2 gap-2">
						<NativeSelect
							aria-label="Предмет расчёта"
							value={receipt.paymentSubject}
							onChange={(event) =>
								setReceipt({ paymentSubject: event.target.value as PaymentSubject })
							}
						>
							{PAYMENT_SUBJECTS.map((subject) => (
								<option key={subject} value={subject}>
									{SUBJECT_LABELS[subject]}
								</option>
							))}
						</NativeSelect>
						<NativeSelect
							aria-label="Способ расчёта"
							value={receipt.paymentMode}
							onChange={(event) => setReceipt({ paymentMode: event.target.value as PaymentMode })}
						>
							{PAYMENT_MODES.map((mode) => (
								<option key={mode} value={mode}>
									{MODE_LABELS[mode]}
								</option>
							))}
						</NativeSelect>
					</div>
					<NativeSelect
						aria-label="Система налогообложения"
						value={receipt.taxSystemCode ?? ""}
						onChange={(event) =>
							onChange({
								...config,
								receipt: withField(
									receipt as Config,
									"taxSystemCode",
									event.target.value ? Number(event.target.value) : undefined,
								),
							})
						}
					>
						<option value="">Налогообложение как в настройках ЮKassa</option>
						{TAX_SYSTEM_CODES.map((code) => (
							<option key={code} value={code}>
								{TAX_LABELS[code]}
							</option>
						))}
					</NativeSelect>
					<NativeSelect
						aria-label="Куда отправить чек"
						value={receipt.contact}
						onChange={(event) => setReceipt({ contact: event.target.value as "email" | "phone" })}
					>
						<option value="email">Чек на email покупателя</option>
						<option value="phone">Чек на телефон покупателя</option>
					</NativeSelect>
					<Input
						aria-label="Строка в чеке"
						placeholder="Строка в чеке: по умолчанию название счёта"
						maxLength={MAX_RECEIPT_ITEM_LENGTH}
						value={receipt.itemDescription ?? ""}
						onChange={(event) =>
							onChange({
								...config,
								receipt: withField(
									receipt as Config,
									"itemDescription",
									event.target.value || undefined,
								),
							})
						}
					/>
					<IssueText issues={issuesAt(issues, "receipt", true)} />
				</div>
			) : null}
		</>
	)
}
